import type { OffsetConfig } from '../../shared/OffsetConfigSection';

type HolidayOffsetMethod = 'offset_overtime' | 'guaranteed_rate';

export interface HolidayOffsetState {
  holidayOffsetAmount: number | null | undefined;
  holidayMultiplierFloorCode: string | null;
  holidayMultiplierFloorCustomValue: number | null | undefined;
}

export interface HolidayOffsetCallbacks {
  onHolidayOffsetAmountChange?: (amount: number) => void;
  onHolidayMultiplierFloorCodeChange: (code: string | null) => void;
  onHolidayMultiplierFloorCustomValueChange?: (value: number | null) => void;
}

export function normalizeOvertimeOffset(raw: number | null | undefined): number {
  if (raw == null || !Number.isFinite(raw)) return 1;
  const trunc = Math.trunc(raw);
  return Math.max(1, trunc);
}

export function buildHolidayOffsetConfig(
  method: HolidayOffsetMethod,
  state: HolidayOffsetState,
): OffsetConfig {
  switch (method) {
    case 'offset_overtime':
      return {
        floor_source: 'none',
        overtime_offset: normalizeOvertimeOffset(state.holidayOffsetAmount),
        floor_tier_code: null,
        floor_tier_custom_value: null,
      };
    case 'guaranteed_rate':
      return {
        floor_source: 'fixed_tier',
        overtime_offset: 0,
        floor_tier_code: state.holidayMultiplierFloorCode,
        floor_tier_custom_value: state.holidayMultiplierFloorCustomValue ?? null,
      };
  }
}

export function handleHolidayOffsetChange(
  method: HolidayOffsetMethod,
  next: OffsetConfig,
  state: HolidayOffsetState,
  callbacks: HolidayOffsetCallbacks,
): void {
  if (method === 'offset_overtime') {
    const current = normalizeOvertimeOffset(state.holidayOffsetAmount);
    if (next.overtime_offset !== current) {
      callbacks.onHolidayOffsetAmountChange?.(next.overtime_offset);
    }
    return;
  }
  if (method === 'guaranteed_rate') {
    if (next.floor_tier_code !== state.holidayMultiplierFloorCode) {
      callbacks.onHolidayMultiplierFloorCodeChange(next.floor_tier_code);
    }
    const currentCustom = state.holidayMultiplierFloorCustomValue ?? null;
    if (next.floor_tier_custom_value !== currentCustom) {
      callbacks.onHolidayMultiplierFloorCustomValueChange?.(next.floor_tier_custom_value);
    }
    return;
  }
}
